import { Box, Tab, Tabs, styled } from 'opensolar-ui'
import { useState } from 'react'
import { TranslateType } from '../../types'
import { formatCurrencyWithSymbol, roundToDecimalPlaces } from '../../utils'
import { SelectedProjectLike } from '../types'

interface SelectedPaymentOptionLike {
  payment_type: string
  utility_cost_total_current: number
  utility_cost_total_proposed: number
  net_cost_to_customer: number
  regular_payment: number
  loan_term?: number
  term_years?: number
  net_profit: number
  cash_flows: number[]
  net_present_value: number
  discounted_payback_period?: number | null
  total_return_on_investment?: number | null
  internal_rate_of_return?: number | null
}

type PropTypes = {
  showFinancialBenefitsDetailed: boolean
  translate: TranslateType
  hasPhoenix: boolean
  selectedPaymentOption: SelectedPaymentOptionLike
  layout: number
  fullCalcReady: boolean
  currencySymbol: string
  selectedProject: SelectedProjectLike
  classNames?: { container?: string; label?: string; detailedContainer?: string; financialBenefits?: string }
}

const convertToMonths = (value) => {
  let decimal = value % 1
  let months = Math.round(decimal * 12)
  return months
}

const StyledTabs = styled(Tabs)({
  padding: '2px 6px',
  borderRadius: 55,
})
const StyledTab = styled(Tab)({
  borderRadius: 55,
  fontSize: 12,
  padding: '6px 10px',
  height: 28,
})

const DETAILED_FINANCIALS_TAB = [
  {
    label: 'Simple Financials',
    value: 'simple_financials',
  },
  {
    label: 'Detailed Financials',
    value: 'detailed_financials',
  },
]

const FinancialBenefitsLegend = (props: PropTypes) => {
  const { showFinancialBenefitsDetailed, translate, selectedProject } = props
  const [displayDetailTab, setDisplayDetailTab] = useState(DETAILED_FINANCIALS_TAB[0].value)
  const displayDetail = displayDetailTab === 'detailed_financials'

  const language = selectedProject.language || 'en'
  const discountedPaybackPeriod = props.selectedPaymentOption.discounted_payback_period ?? 0
  const discountedPaybackPeriodToYears = Math.floor(discountedPaybackPeriod)
  const discountedPaybackPeriodToMonths = convertToMonths(discountedPaybackPeriod)

  return (
    <div className={props?.classNames?.container}>
      {showFinancialBenefitsDetailed && (
        <Box alignItems="center" display="flex" justifyContent="center">
          <StyledTabs
            onChange={(_, newValue) => {
              setDisplayDetailTab(newValue)
            }}
            type="segmented"
            value={displayDetailTab}
          >
            {DETAILED_FINANCIALS_TAB.map((tab) => (
              <StyledTab
                isSelected={tab.value === displayDetailTab}
                key={tab.value}
                label={props.translate(tab.label)}
                value={tab.value}
              />
            ))}
          </StyledTabs>
        </Box>
      )}
      {!displayDetail && !props.hasPhoenix && (
        <div style={{ height: 130 }}>
          <div
            className={props.classNames?.financialBenefits ?? 'Financial-Benefits'}
            style={{ top: showFinancialBenefitsDetailed ? '' : 10 }}
          >
            {props.selectedPaymentOption.payment_type !== 'regular_payment' && (
              <div
                style={{ flex: '1 0 auto', textAlign: 'center', width: '25%' }}
                className={props.layout === 1 ? 'smallFF' : ''}
              >
                {props.fullCalcReady ? (
                  <div>
                    <div className={props.layout <= 2 ? 'feature-figure-small' : 'feature-figure'}>
                      {formatCurrencyWithSymbol(
                        props.selectedPaymentOption.utility_cost_total_current -
                          props.selectedPaymentOption.utility_cost_total_proposed,
                        props.currencySymbol,
                        language,
                        0
                      )}
                    </div>
                    <div className={props?.classNames?.label}>{translate('Utility Bill Savings')}</div>
                  </div>
                ) : (
                  <div
                    className="loading-feature-figure"
                    style={{ margin: props.layout === 1 ? '10px auto' : '14px auto' }}
                  />
                )}
              </div>
            )}

            {props.selectedPaymentOption.payment_type !== 'regular_payment' && (
              <div
                className={props.layout <= 2 ? 'feature-figure-small' : 'feature-figure'}
                style={{
                  flex: '1 0 auto',
                  width: '30px',
                  textAlign: 'center',
                }}
              >
                -
              </div>
            )}

            {props.selectedPaymentOption.payment_type !== 'regular_payment' && (
              <div
                style={{ flex: '1 0 auto', textAlign: 'center', width: '25%' }}
                className={props.layout === 1 ? 'smallFF' : ''}
              >
                {props.fullCalcReady ? (
                  <div>
                    <div className={props.layout <= 2 ? 'feature-figure-small' : 'feature-figure'}>
                      {formatCurrencyWithSymbol(
                        props.selectedPaymentOption.net_cost_to_customer,
                        props.currencySymbol,
                        language,
                        0
                      )}
                    </div>
                    <div className={props?.classNames?.label}>
                      {props.selectedPaymentOption.regular_payment > 0
                        ? translate('Total Payments over years', {
                            years: props.selectedPaymentOption.loan_term
                              ? roundToDecimalPlaces(props.selectedPaymentOption.loan_term || 0, 0, language)
                              : roundToDecimalPlaces(props.selectedPaymentOption.term_years || 0, 0, language),
                          })
                        : translate('Net System Cost')}
                    </div>
                  </div>
                ) : (
                  <div
                    className="loading-feature-figure"
                    style={{ margin: props.layout === 1 ? '10px auto' : '14px auto' }}
                  />
                )}
              </div>
            )}

            {props.selectedPaymentOption.payment_type !== 'regular_payment' && (
              <div
                className={props.layout <= 2 ? 'feature-figure-small' : 'feature-figure'}
                style={{
                  flex: '1 0 auto',
                  width: '30px',
                  textAlign: 'center',
                }}
              >
                =
              </div>
            )}
            <div
              style={{ flex: '1 0 auto', textAlign: 'center', width: '25%' }}
              className={props.layout === 1 ? 'smallFF' : ''}
            >
              {props.fullCalcReady ? (
                <div className={props.selectedPaymentOption.net_profit > 0 ? 'green' : ''}>
                  <div className={props.layout <= 2 ? 'feature-figure-small' : 'feature-figure'}>
                    <span>
                      {formatCurrencyWithSymbol(
                        Math.abs(props.selectedPaymentOption.net_profit),
                        props.currencySymbol,
                        language,
                        0
                      )}
                    </span>
                  </div>
                  <div className={props.selectedPaymentOption.net_profit !== undefined ? props?.classNames?.label : ''}>
                    {props.selectedPaymentOption.net_profit > 0 && (
                      <span>
                        {translate('Estimated Net Savings')}
                        {props.selectedPaymentOption.payment_type === 'regular_payment' && (
                          <span>
                            {' '}
                            {'over ' +
                              roundToDecimalPlaces(props.selectedPaymentOption.cash_flows.length - 1, 0, language) +
                              ' years'}
                          </span>
                        )}
                      </span>
                    )}
                    {props.selectedPaymentOption.net_profit <= 0 && <span>{translate('Clean Energy Premium')}</span>}
                  </div>
                </div>
              ) : (
                <div
                  className="loading-feature-figure"
                  style={{ margin: props.layout === 1 ? '10px auto' : '14px auto' }}
                />
              )}
            </div>
          </div>
        </div>
      )}
      {showFinancialBenefitsDetailed && displayDetail && (
        <div
          style={{
            width: '100%',
            minWidth: 300,
            display: 'flex',
            flexDirection: 'row',
            margin: 'auto',
            fontSize: 12,
            height: 130,
            position: 'relative',
          }}
          className={(props.layout === 1 ? 'smallFF ' : '') + props?.classNames?.detailedContainer}
        >
          <div
            style={{
              flex: '1 0 0',
              textAlign: 'center',
              padding: '35px 5px 0 5px',
              display: 'flex',
              overflow: 'visible',
              justifyContent: 'center',
              flexDirection: 'row',
              alignSelf: 'flex-start',
            }}
          >
            <div style={{ overflow: 'visible' }}>
              {props.fullCalcReady ? (
                <div>
                  <div className={props.layout <= 2 ? 'feature-figure-small' : 'feature-figure'}>
                    {formatCurrencyWithSymbol(
                      props.selectedPaymentOption.net_present_value,
                      props.currencySymbol,
                      language,
                      0
                    )}
                  </div>
                  <div className={props?.classNames?.label}>{translate('Net Present Value')}</div>
                </div>
              ) : (
                <div
                  className="loading-feature-figure"
                  style={{ margin: props.layout === 1 ? '10px auto' : '14px auto' }}
                />
              )}
            </div>
          </div>
          {discountedPaybackPeriod > 0 && (
            <div
              style={{
                flex: '1 0 0',
                textAlign: 'center',
                padding: '35px 5px 0 5px',
                display: 'flex',
                overflow: 'visible',
                justifyContent: 'center',
                flexDirection: 'row',
                alignSelf: 'flex-start',
              }}
            >
              <div style={{ overflow: 'visible' }}>
                {props.fullCalcReady ? (
                  <div>
                    <div
                      className={props.layout <= 2 ? 'feature-figure-small' : 'feature-figure'}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      {discountedPaybackPeriodToYears}
                      <div style={{ marginLeft: 5 }}>
                        <div style={{ fontSize: 17, textAlign: 'left' }}>
                          {discountedPaybackPeriodToYears > 1 ? translate('Years') : translate('Year')}
                        </div>
                        {discountedPaybackPeriodToMonths > 0 && (
                          <div style={{ fontSize: 13, textAlign: 'left' }}>
                            {discountedPaybackPeriodToMonths}{' '}
                            {discountedPaybackPeriodToMonths > 1 ? translate('Months') : translate('Month')}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className={props?.classNames?.label}>{translate('Discounted Payback Period')}</div>
                  </div>
                ) : (
                  <div
                    className="loading-feature-figure"
                    style={{ margin: props.layout === 1 ? '10px auto' : '14px auto' }}
                  />
                )}
              </div>
            </div>
          )}
          {props.selectedPaymentOption.total_return_on_investment != null &&
            props.selectedPaymentOption.total_return_on_investment > 0 && (
              <div
                style={{
                  flex: '1 0 0',
                  textAlign: 'center',
                  padding: '35px 5px 0 5px',
                  display: 'flex',
                  overflow: 'visible',
                  justifyContent: 'center',
                  flexDirection: 'row',
                  alignSelf: 'flex-start',
                }}
              >
                <div style={{ overflow: 'visible' }}>
                  {props.fullCalcReady ? (
                    <div>
                      <div className={props.layout <= 2 ? 'feature-figure-small' : 'feature-figure'}>
                        {roundToDecimalPlaces(
                          100 * (props.selectedPaymentOption.total_return_on_investment || 0),
                          0,
                          language
                        )}
                        %
                      </div>
                      <div className={props?.classNames?.label}>{translate('Total Return on Investment')}</div>
                    </div>
                  ) : (
                    <div
                      className="loading-feature-figure"
                      style={{ margin: props.layout === 1 ? '10px auto' : '14px auto' }}
                    />
                  )}
                </div>
              </div>
            )}
          {props.selectedPaymentOption.internal_rate_of_return != null &&
            props.selectedPaymentOption.internal_rate_of_return > 0 && (
              <div
                style={{
                  flex: '1 0 0',
                  textAlign: 'center',
                  padding: '35px 5px 0 5px',
                  display: 'flex',
                  overflow: 'visible',
                  justifyContent: 'center',
                  flexDirection: 'row',
                  alignSelf: 'flex-start',
                }}
              >
                <div style={{ overflow: 'visible' }}>
                  {props.fullCalcReady ? (
                    <div>
                      <div className={props.layout <= 2 ? 'feature-figure-small' : 'feature-figure'}>
                        {roundToDecimalPlaces(
                          100 * (props.selectedPaymentOption.internal_rate_of_return || 0),
                          1,
                          language
                        )}
                        %
                      </div>
                      <div className={props?.classNames?.label}>{translate('Rate of Return on Investment')}</div>
                    </div>
                  ) : (
                    <div
                      className="loading-feature-figure"
                      style={{ margin: props.layout === 1 ? '10px auto' : '14px auto' }}
                    />
                  )}
                </div>
              </div>
            )}
        </div>
      )}
    </div>
  )
}

export default FinancialBenefitsLegend
